import { useState } from 'react';
import { NavLink } from 'react-router';
import { Badge, Group, Stack, Collapse } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { NavigationLink } from '../hooks/useNavigationLinks';
import classes from '../UsersMenu.module.css';

interface MobileMenuProps {
  links: NavigationLink[];
  onClose: () => void;
}

export const MobileMenu = ({ links, onClose }: MobileMenuProps) => {
  const [expandedLinks, setExpandedLinks] = useState<Record<string, boolean>>(
    {}
  );

  const toggleSubMenu = (label: string) => {
    setExpandedLinks((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const renderMobileMenuItem = (
    item: NavigationLink,
    depth: number = 0,
    index: number = 0
  ) => {
    const itemKey = `${item.label}-${depth}-${index}`;

    if (item.links) {
      return (
        <div key={itemKey}>
          <NavLink
            to="#"
            className={depth === 0 ? classes.mobileLink : classes.mobileSubLink}
            onClick={(e) => {
              e.preventDefault();
              toggleSubMenu(itemKey);
            }}
            style={{
              paddingLeft: depth > 0 ? `${depth * 16 + 16}px` : undefined,
            }}
          >
            {item.label}
          </NavLink>
          <Collapse in={expandedLinks[itemKey]}>
            <Stack gap={0}>
              {item.links.map((subItem, subIndex) =>
                renderMobileMenuItem(subItem, depth + 1, subIndex)
              )}
            </Stack>
          </Collapse>
        </div>
      );
    }

    // Handle function-based onClick or string-based navigation
    const isFunction = typeof item.link === 'function';

    if (isFunction) {
      return (
        <NavLink
          key={itemKey}
          to="#"
          className={depth === 0 ? classes.mobileLink : classes.mobileSubLink}
          onClick={(e) => {
            e.preventDefault();
            (item.link as () => void)();
            onClose();
          }}
          style={{
            paddingLeft: depth > 0 ? `${depth * 16 + 16}px` : undefined,
            color:
              item.color === 'red'
                ? 'var(--mantine-color-red-6)'
                : item.color === 'orange'
                  ? 'var(--mantine-color-orange-6)'
                  : undefined,
          }}
        >
          {item.label}
        </NavLink>
      );
    }

    return (
      <NavLink
        key={itemKey}
        to={item.link as string}
        className={depth === 0 ? classes.mobileLink : classes.mobileSubLink}
        onClick={() => onClose()}
        style={{ paddingLeft: depth > 0 ? `${depth * 16 + 16}px` : undefined }}
      >
        {item.label}
      </NavLink>
    );
  };

  const mobileItems = links.map((link, linkIndex) => {
    if (link.links) {
      return (
        <div key={`${link.label}-${linkIndex}`}>
          <NavLink
            to="#"
            className={classes.mobileLink}
            onClick={(e) => {
              e.preventDefault();
              toggleSubMenu(`${link.label}-${linkIndex}`);
            }}
            style={{ display: 'flex', alignItems: 'center', width: '100%' }}
          >
            <Group gap={6} wrap="nowrap" align="center" style={{ flex: 1 }}>
              <span>{link.label}</span>
              {link.badge ? (
                <Badge
                  size="xs"
                  variant="filled"
                  color="blue"
                  radius="sm"
                  tt="uppercase"
                >
                  {link.badge}
                </Badge>
              ) : null}
            </Group>
            <IconChevronDown
              size={14}
              stroke={1.5}
              style={{
                transform: expandedLinks[`${link.label}-${linkIndex}`]
                  ? 'rotate(180deg)'
                  : 'none',
                transition: 'transform 200ms ease',
              }}
            />
          </NavLink>
          <Collapse in={expandedLinks[`${link.label}-${linkIndex}`]}>
            <Stack gap={0}>
              {link.links.map((item, itemIndex) =>
                renderMobileMenuItem(item, 1, itemIndex)
              )}
            </Stack>
          </Collapse>
        </div>
      );
    }

    return (
      <NavLink
        key={`${link.label}-${linkIndex}`}
        to={link.link as string}
        className={classes.mobileLink}
        onClick={() => onClose()}
      >
        {link.label}
      </NavLink>
    );
  });

  return <>{mobileItems}</>;
};
